import { queryOptions, useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  SupportModule,
  SupportTicketDetailSchema,
  SupportTicketListResponse,
  SupportTicketQuery,
} from './schemas/support-query-schema';

// Support Tickets API
export function getSupportTicketsQuery(params: SupportTicketQuery) {
  const { companyId, contactId, page, per_page, order_by, order_type, include } = params;

  const searchParams = new URLSearchParams();
  if (companyId) {
    searchParams.set('company_id', companyId);
  }
  if (contactId) {
    searchParams.set('contact_id', contactId);
  }
  searchParams.set('page', page.toString());
  searchParams.set('per_page', per_page.toString());
  searchParams.set('order_by', order_by);
  searchParams.set('order_type', order_type);
  searchParams.set('include', include || 'requester,company,agent');

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  return {
    queryKey: ['SUPPORT_TICKETS', searchParams.toString()],
    queryFn: async (): Promise<SupportTicketListResponse> => {
      if (!companyId && !contactId) {
        return {
          data: [],
          meta: {
            current_page: 1,
            from: 1,
            last_page: 1,
            links: [],
            path: '',
            per_page: 0,
            to: 0,
            total: 0,
          },
          links: {
            first: '',
            last: '',
            prev: null,
            next: null,
          },
        };
      }
      const res = await apiClient.get<SupportTicketListResponse>({
        endpoint: `/api/v1/freshdesk/tickets?${searchParams.toString()}`,
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export function useGetSupportTickets(apiParamStr: string, freshdeskId?: string) {
  const params: SupportTicketQuery = {
    companyId: freshdeskId || '',
    page: 1,
    per_page: 100,
    order_by: 'updated_at',
    order_type: 'desc',
  };

  // Parse apiParamStr to extract pagination and sorting
  const urlParams = new URLSearchParams(apiParamStr.replace('?', ''));

  if (urlParams.has('page')) {
    params.page = parseInt(urlParams.get('page') || '1', 10);
  }

  if (urlParams.has('per_page')) {
    params.per_page = parseInt(urlParams.get('per_page') || '25', 10);
  }

  if (urlParams.has('sort')) {
    const sort = urlParams.get('sort') || '';
    if (sort.startsWith('-')) {
      params.order_by = sort.substring(1);
      params.order_type = 'desc';
    } else {
      params.order_by = sort;
      params.order_type = 'asc';
    }
  }

  return useSuspenseQuery(getSupportTicketsQuery(params));
}

export function getSupportTicketDetailQuery(ticketId: string) {
  return {
    queryKey: ['SUPPORT_TICKET_DETAIL', ticketId],
    queryFn: async (): Promise<SupportTicketDetailSchema> => {
      const res = await apiClient.get<SupportTicketDetailSchema>({
        endpoint: `/api/v1/freshdesk/tickets/${ticketId}`,
        params: { include: 'requester,company,conversations,group,agent' },
        preventAuditLog: true,
      });
      return res;
    },
  };
}

export function useGetSupportTicketDetail(ticketId: string) {
  return useSuspenseQuery(getSupportTicketDetailQuery(ticketId));
}

export const invalidateSupportTickets = () => {
  queryClient.invalidateQueries({ queryKey: ['SUPPORT_TICKETS'] });
};

export const invalidateSupportTicketDetail = (ticketId: string) => {
  queryClient.invalidateQueries({ queryKey: ['SUPPORT_TICKET_DETAIL', ticketId] });
};

type SupportTicketsInfiniteQueryParams = {
  module: SupportModule;
  freshdeskId: string;
  sort?: string;
  per_page?: number;
  filters?: string;
  search?: string;
};

export const useSupportTicketsInfiniteQuery = ({
  module,
  freshdeskId,
  sort = '-updated_at',
  per_page = 25,
  filters = '',
  search = '',
}: SupportTicketsInfiniteQueryParams) =>
  useSuspenseInfiniteQuery<SupportTicketListResponse, ApiError>({
    queryKey: ['SUPPORT_TICKETS_INFINITE', module, freshdeskId, sort, per_page, filters, search],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (module === 'companies') {
        searchParams.set('company_id', freshdeskId);
      } else if (module === 'contacts') {
        searchParams.set('contact_id', freshdeskId);
      }
      searchParams.set('page', (pageParam as number).toString());
      searchParams.set('per_page', per_page.toString());

      // Parse sort parameter
      if (sort.startsWith('-')) {
        searchParams.set('order_by', sort.substring(1));
        searchParams.set('order_type', 'desc');
      } else {
        searchParams.set('order_by', sort);
        searchParams.set('order_type', 'asc');
      }

      searchParams.set('include', 'requester,company,agent');

      // Add search if provided
      if (search) {
        searchParams.set('search', search);
      }

      // Add filters if provided
      if (filters) {
        const filtersParams = new URLSearchParams(filters);
        filtersParams.forEach((value, key) => {
          searchParams.set(key, value);
        });
      }

      return apiClient.get<SupportTicketListResponse>({
        endpoint: `/api/v1/freshdesk/tickets?${searchParams.toString()}`,
        preventAuditLog: true,
      });
    },
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page ? lastPage.meta.current_page + 1 : undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.meta.current_page > 1 ? firstPage.meta.current_page - 1 : undefined,
  });

export const getSupportTicketsQueryOptions = ({ companyId, contactId }: { companyId?: string; contactId?: string }) =>
  queryOptions(
    getSupportTicketsQuery({
      companyId: companyId,
      contactId: contactId,
      page: 1,
      per_page: 500,
      order_by: 'updated_at',
      order_type: 'desc',
      include: 'requester,company,agent',
    })
  );

export const getSupportTicketsAllQueryOptions = () =>
  queryOptions(
    getSupportTicketsQuery({
      companyId: '',
      contactId: '',
      page: 1,
      per_page: 500,
      order_by: 'updated_at',
      order_type: 'desc',
      include: 'requester,company,agent',
    })
  );
